const COUNT_ATTR = /data-storefront-results-count=["'](\d+)["']/;

function getThemeSearchUrl() {
  if (typeof window === 'undefined') return '';
  return window.theme?.routes?.searchUrl || window.routes?.search_url || '';
}

function normalizeSearchPath(basePath) {
  const pathOnly = basePath.split('?')[0] || '/search';
  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
}

function getSearchBasePath() {
  return normalizeSearchPath(getThemeSearchUrl() || '/search');
}

/**
 * Storefront search URL (non-predictive). Omits `type` for combined “all” results.
 * @see https://shopify.dev/docs/storefronts/themes/navigation-search/search
 */
function buildSearchUrl(query, options = {}) {
  const params = new URLSearchParams();
  params.set('q', query);

  if (options.type) {
    params.set('type', options.type);
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${getSearchBasePath()}?${params.toString()}`;
}

function parseResultsCountFromSearchHtml(html) {
  const scopedMatch =
    html.match(/id="main-search-results"[^>]*data-storefront-results-count=["'](\d+)["']/) ||
    html.match(/data-storefront-results-count=["'](\d+)["'][^>]*id="main-search-results"/);
  const countMatch = scopedMatch || html.match(COUNT_ATTR);
  if (!countMatch) return NaN;
  const parsedCount = Number(countMatch[1]);
  return Number.isFinite(parsedCount) ? parsedCount : NaN;
}

async function fetchSearchResultCount(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(String(response.status));
  return parseResultsCountFromSearchHtml(await response.text());
}

class SearchBar extends HTMLElement {
  connectedCallback() {
    void this.#syncFromStorefront();
  }

  #applyCount(tabItem, resultCount) {
    if (!Number.isFinite(resultCount)) return;
    tabItem.dataset.resultsCount = String(resultCount);
    if (resultCount <= 0) {
      tabItem.querySelector('.button').classList.add('disabled');
    } else {
      tabItem.querySelector('.button').classList.remove('disabled');
    }
  }

  async #syncFromStorefront() {
    const query = (this.dataset.searchTerms || '').trim();
    if (!query) return;

    let countAllTypes;
    let countProducts;
    let countArticles;
    let countPages;

    try {
      [countAllTypes, countProducts, countArticles, countPages] = await Promise.all([
        fetchSearchResultCount(buildSearchUrl(query)),
        fetchSearchResultCount(buildSearchUrl(query, { type: 'product' })),
        fetchSearchResultCount(buildSearchUrl(query, { type: 'article' })),
        fetchSearchResultCount(buildSearchUrl(query, { type: 'page' })),
      ]);
    } catch {
      for (const tabItem of this.querySelectorAll('li[data-search-type]')) {
        this.#applyCountFromDataset(tabItem);
      }
      return;
    }

    const countsBySearchType = {
      all: countAllTypes,
      product: countProducts,
      article: countArticles,
      page: countPages,
    };

    for (const tabItem of this.querySelectorAll('li[data-search-type]')) {
      const searchType = tabItem.dataset.searchType;
      const fetchedCount = countsBySearchType[searchType];
      if (Number.isFinite(fetchedCount)) {
        this.#applyCount(tabItem, fetchedCount);
      } else {
        this.#applyCountFromDataset(tabItem);
      }
    }
  }

  #applyCountFromDataset(tabItem) {
    const rawCount = tabItem.dataset.resultsCount;
    const parsedCount = rawCount === undefined || rawCount === '' ? NaN : Number(rawCount);
    if (!Number.isFinite(parsedCount)) {
      tabItem.querySelector('.button').classList.add('disabled');
      return;
    }
    this.#applyCount(tabItem, parsedCount);
  }
}

customElements.define('search-bar', SearchBar);
